import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { AppBaseComponent } from '@common/components';
import { ApiService, BreadcrumbService, NotifyService, ProfileService } from '@shared/services';
import { Observable, catchError, finalize, filter, of, switchMap, takeUntil, tap, throwError } from 'rxjs';

@Component({
    selector: 'app-base-overview',
    template: '',
})
export abstract class BaseOverviewComponent<T> extends AppBaseComponent implements OnDestroy {
    @ViewChild(MatSort) sort!: MatSort;
    @ViewChild(MatPaginator) paginator!: MatPaginator;

    tableSourceOptions: string[] = [];

    protected readonly profileService = inject(ProfileService);
    protected readonly dialogRef = inject(MatDialog);
    protected readonly apiService = inject(ApiService);
    protected readonly breadcrumbService = inject(BreadcrumbService);
    protected readonly notifyService = inject(NotifyService);
    protected readonly matDialog = inject(MatDialog);
    protected readonly router = inject(Router);

    // Common properties
    data: T[] = [];
    dataSource?: MatTableDataSource<T>;
    isProcessing = false;

    initDataSource(data: T[]): void {
        this.dataSource = new MatTableDataSource(data);
        if (this.sort) {
            this.dataSource.sort = this.sort;
        }
        if (this.paginator) {
            this.dataSource.paginator = this.paginator;
        }
    }

    onRefresh(): void {
        this.loadData();
    }

    protected handleEditDialog<TResult>(
        dialogRef: MatDialogRef<any>,
        apiCall: (request: any) => Observable<TResult>,
        options: {
            errorMsg: string;
            successMsg: string;
        },
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        dialogRef.componentInstance.submit$
            .pipe(
                tap(() => {
                    dialogRef.componentInstance.isProcessing = true;
                }),
                switchMap((userRequest: any) => {
                    return apiCall(userRequest).pipe(
                        catchError(() => {
                            this.notifyService.error(options.errorMsg);
                            return of(null);
                        }),
                        finalize(() => {
                            dialogRef.componentInstance.isProcessing = false;
                        }),
                    );
                }),
                filter((result) => !!result),
                takeUntil(this.destroyed$),
            )
            .subscribe(() => {
                this.loadData();
                dialogRef.close();
                this.notifyService.info(options.successMsg);
            });
    }

    protected handleDeleteConfirmation<TResult>(deleteApiCall: Observable<TResult>, deletePopupComponent: any, editDialogRef?: MatDialogRef<any>): void {
        const dialogRef = this.matDialog.open(deletePopupComponent, {
            data: { message: 'Are you sure you want to delete this record?' },
            width: '450px',
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((result) => {
                if (result) {
                    deleteApiCall
                        .pipe(
                            catchError(() => {
                                this.notifyService.error('Error while deleting record');
                                return throwError(() => new Error('Delete failed'));
                            }),
                            takeUntil(this.destroyed$),
                        )
                        .subscribe(() => {
                            editDialogRef?.close();
                            this.loadData();
                            this.notifyService.info('Record deleted successfully');
                        });
                }
            });
    }

    protected handleCannotDeleteWarning(warningPopupComponent: any): void {
        const confirmDialog = this.matDialog.open(warningPopupComponent, {
            data: {
                title: 'Warning',
                message: 'This record has relations in other tables and cannot be deleted.',
            },
        });
        confirmDialog.afterClosed().subscribe();
    }

    abstract loadData(): void;

    override ngOnDestroy(): void {
        super.ngOnDestroy();
    }
}
